<template>
  <div id="app" :class="tenantDetails.Theme || ''">
    <div>
      <div v-if="showSessionInactive" class="inactivity-warning">
        <b-alert class="inactivity-warning-modal" show variant="info">
          <div class="inactivity-warning-text">
            <h4>Your session has expired. Please refresh.</h4>
            <dn-function-button extra-classes="btn btn-large" :callback-fn="refreshBrowser" text="Refresh" />
          </div>
        </b-alert>
      </div>
      <div>
        <div v-if="$route.path.indexOf('/new-uca-landing-page') === 0">
          <NewUcaLandingPage :theme="tenantDetails.Theme" />
        </div>
        <div v-else-if="!isAuthenticated && !loading">
          <BaseNotAuthenticatedPage :theme="tenantDetails.Theme" :custom-message="authErrorMessage" />
        </div>
        <div v-else-if="loading">
          <BaseLoadingPage :theme="tenantDetails.Theme" :showIEWarning="showIEWarning" />
        </div>
        <!-- cart pages use different header/footer -->
        <div v-else-if="$route.path.indexOf('/shopping-cart') === 0">
          <Analytics
            v-if="tenantDetails.GoogleAnalyticsId != undefined"
            :google-analytics-id="tenantDetails.GoogleAnalyticsId"
            :ga4-id="tenantDetails.GA4Id"
          />
          <CartHeader />
          <Message />
          <router-view :key="$route.fullPath" :theme="tenantDetails.Theme" :cost-center="tenantDetails.McfCostCenter" />
          <CartFooter />
        </div>
        <div v-else :class="displaySideCart() && sideCartEnabled() && 'shift-left'">
          <message-banner />
          <side-cart v-if="displaySideCart()" :class="!sideCartEnabled() && 'side-cart__hide'" />
          <button
            v-if="displaySideCart()"
            :class="['app__expand-sc', 'btn-smallGrayButton', sideCartEnabled() ? 'app__collapse-sc' : '']"
            title="Show Side Cart"
            @click="toggleSideCart()"
          >
            <fa-icon icon="chevron-down" />
          </button>
          <Analytics
            v-if="tenantDetails.GoogleAnalyticsId != undefined"
            :google-analytics-id="tenantDetails.GoogleAnalyticsId"
            :ga4-id="tenantDetails.GA4Id"
          />
          <BaseHeader :tenant-details="tenantDetails" />
          <Message />
          <router-view :key="$route.fullPath" :theme="tenantDetails.Theme" :cost-center="tenantDetails.McfCostCenter" />
          <BaseFooter />
          <NewPartListModal />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from "vue";

import Analytics from "@/components/Analytics";
import BaseNotAuthenticatedPage from "@/components/BaseNotAuthenticatedPage";
import BaseLoadingPage from "@/components/BaseLoadingPage";
import BaseHeader from "@/components/Header/BaseHeader";
import BaseFooter from "@/components/BaseFooter";
import Message from "@/components/Message";
import NewPartListModal from "@/components/Modals/NewPartListModal";
import CartFooter from "@/components/Cart/CartFooter/CartFooter.vue";
import CartHeader from "@/components/Cart/CartHeader/CartHeader.vue";
import SideCart from "@/components/SideCart/SideCart.vue";
import { mapGetters } from "vuex";
import { HubConnectionBuilder, LogLevel } from "@microsoft/signalr";
import { AlertService } from "@/services/AlertService";
import { FeatureFlagService } from "@/services/FeatureFlagService";
import DnFunctionButton from "@/components/Elements/DnFunctionButton.vue";
import { NextivaService } from "@/services/NextivaService";
import { constants } from "@/const";
import { ErrorLogService, UserSettingsService, SettingsService, LoginService, UserService } from "./services";
import NewUcaLandingPage from "@/components/NewUcaLandingPage.vue";
import MessageBanner from "@/components/Header/MessageBanner/MessageBanner.vue";

export default {
  name: "App",
  components: {
    BaseNotAuthenticatedPage,
    BaseLoadingPage,
    BaseHeader,
    BaseFooter,
    CartFooter,
    CartHeader,
    NewPartListModal,
    Message,
    Analytics,
    DnFunctionButton,
    SideCart,
    NewUcaLandingPage,
    MessageBanner,
  },
  data: () => ({
    loading: true,
    connection: "",
    message: "",
    features: Object.values(constants.FEATURE_NAMES),
    showIEWarning: false,
    showSessionInactive: false,
    isAuthenticated: false,
    authErrorMessage: "",
  }),
  computed: {
    ...mapGetters([
      "user",
      "tenantDetails",
      "activeAccountId",
      "audienceIds",
      "McfCostCenterId",
      "isMobile",
      "showSideCart",
      "showSideCartValue",
      "cartSummary",
      "activeDealer"
    ]),
    isIdle() {
      return this.$store.state.idleVue.isIdle;
    },
  },
  watch: {
    isIdle() {
      if (this.isIdle) {
        this.connection.stop();
        this.showSessionInactive = true;
      }
    },
    loading() {
      this.setNextivaMobileStyling();
    },
  },
  updated() {
    //clears hash for pages that use DnRouterLink to refresh components when click on the same page the use is already on
    window.location.hash = "";
  },
  beforeCreate() {},
  async created() {
    // We need to force the login redirect for old hostnames once logisnextdealers.com is available
    const obsoleteHostNames = [
      "rpionline.com",
      "mcfadealers.com",
      "mcfedealers.com",
      "mcfsdealers.com",
      "ucadealers.com",
    ];

    let isHostnameObsolete = obsoleteHostNames.some((x) => window.location.hostname.includes(x));
    if (isHostnameObsolete) {
      this.$store.dispatch("FORCE_LOGIN_REDIRECT").then((redirectResult) => {
        return;
      });
    } else {
      this.checkAuthentication();
      this.setIsMobile();
      this.setPageTitle();
    }
  },
  methods: {
    checkAuthentication() {
      LoginService.getIsLoggedIn()
        .then(async (result) => {
          var isLoggedIn = result;

          if (!isLoggedIn) {
            if (this.$route.path.indexOf("/new-uca-landing-page") !== 0) {
              var currentUrl = encodeURIComponent(window.location.href);
              window.location.href = `${window.appGlobals.API_URL}login?fromUrl=${currentUrl}`;
            }
          } else {
            this.isAuthenticated = true;

            // load user, available accounts, and set active account
            await this.$store.dispatch("LOAD_USER_AND_AVAILABLE_ACCOUNTS");

            //load general site information
            await this.$store.dispatch("LOAD_TENANT");

            await Promise.all([
              //get all setting and set root catalog id
              SettingsService.getAllSettings().then((result) => {
                this.$store.dispatch("SET_COST_CENTER_ROOT_CATEGORY_ID", {
                  result: result,
                  userId: this.user.UserId,
                });
              }),
              //load audience and exclusions
              this.$store.dispatch("LOAD_AUDIENCE_AND_EXCLUSIONS"),

              //load feature flags
              this.getEnabledFeatures(this.features),

              //load user settings
              this.$store.dispatch("LOAD_USER_SETTINGS"),
              
              //load languages based on user preference
              typeof this.user?.LanguagePreference?.Code !== "undefined"
                ? this.$store.dispatch("LOAD_LANGUAGES", this.user.LanguagePreference.Code)
                : Promise.resolve(),
            ]);

            this.loading = false;

            // after everything is loaded, handle other post-load actions
            this.$nextTick(() => {
              this.isNextivaChatEnabled();
              this.$store.dispatch("GET_PARTS_STORE_ORDER_TOTAL");
              this.$store.dispatch("LOAD_FACING_WAREHOUSE");
              this.$store.dispatch("LOAD_CATALOG", {
                accountId: this.activeAccountId,
                userId: this.user.UserId,
                audienceIds: this.audienceIds,
              });
            });
          }
        })
        .catch((err) => {
          if (!this.activeDealer) {
            this.authErrorMessage = "The account code you are using is not active";
          }
          this.isAuthenticated = false;
          this.loading = false;
        })
        .finally(() => {
          this.setupSignalR();
        });
    },
    async getEnabledFeatures(featureNames) {
      FeatureFlagService.GetFeatureFlags(featureNames, this.activeDealer.AccountSalesOrg.McfCostCenterId, this.activeAccountId);
    },
    hideIEWarning() {
      this.showIEWarning = false;
    },
    refreshBrowser() {
      location.reload();
    },
    setupSignalR() {
      try {
        if (window.appGlobals.ENABLE_SIGNAL_R !== true) {
          return;
        }
        // Start the alert service - this could potentially be moved to main.js
        AlertService.setUpAlertHub();

        // Set our connection to the alert service hub
        this.connection = AlertService.getAlertHub();

        // Creating a new var for 'this' - Need to in order for 'on receive' to work.
        var thisVue = this;
        // Setting the on receive for the hub
        thisVue.connection.on("ReceiveMessage", function (subject, message) {
          thisVue.$store.commit("pushMessage", {
            header: subject,
            message: message,
            IsError: false,
          });
        });

        thisVue.connection.onclose(() => {
          if (!thisVue.isIdle) {
            AlertService.startAlertHub();
          }
        });
      } catch (ex) {
        ErrorLogService.LogException("Failed to connect to SignalR: " + ex);
      }
    },
    async isNextivaChatEnabled() {
      if (this.McfCostCenterId !== 1) {
        NextivaService.SetChatClientVisibility(false);
        return;
      }

      await NextivaService.SetupChatClient();
      NextivaService.SetChatClientVisibility(true);
    },

    displaySideCart() {
      return !this.IsNullOrUndefined(this.cartSummary) && this.cartSummary.ItemCount > 0;
    },

    sideCartEnabled() {
      return this.showSideCart.SettingValue.toString() === "true";
    },

    async toggleSideCart() {
      var enabled = !this.sideCartEnabled();
      // Save user setting
      this.$store.commit("SET_SHOW_SIDE_CART", enabled);
    },

    //Create and save new user setting for side cart
    saveNewShowSideCartSetting() {
      let newShowSideCartSetting = [
        {
          SettingName: "Show Side Cart",
          SettingValue: false,
          UserId: this.user.UserId,
        },
      ];
      this.$store.commit("ADD_USER_SETTINGS", newShowSideCartSetting[0]);
      UserSettingsService.SaveUserSettings(newShowSideCartSetting);
    },

    setIsMobile() {
      // See recommended mobile device detection from MDN, prefering touch detection followed by other fall-backs:
      // https://developer.mozilla.org/en-US/docs/Web/HTTP/Browser_detection_using_the_user_agent
      let isOnMobile = false;

      if ("maxTouchPoints" in navigator) {
        isOnMobile = navigator.maxTouchPoints > 0;
      } else if ("msMaxTouchPoints" in navigator) {
        isOnMobile = navigator.msMaxTouchPoints > 0;
      } else {
        const mQ = matchMedia?.("(pointer:coarse)");

        if (mQ?.media === "(pointer:coarse)") {
          isOnMobile = !!mQ.matches;
        } else if ("orientation" in window) {
          isOnMobile = true; // deprecated, but good fallback
        } else {
          // Only as a last resort, fall back to user agent sniffing
          const UA = navigator.userAgent;

          isOnMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        }
      }

      this.$store.commit("SET_IS_MOBILE", isOnMobile);
    },

    setNextivaMobileStyling() {
      if (this.isMobile) {
        var nextivaDiv = document.getElementById("chat-notification");
        if (!this.IsNullOrUndefined(nextivaDiv)) {
          nextivaDiv.classList.remove("BottomRight");
          nextivaDiv.classList.add("pl-2");
        }
      }
    },

    setPageTitle(name) {
      try {
        var hostName = location.hostname;
        //START localhost
        if (hostName.includes("localhost")) {
          document.title = "Local DN";
        }
        //END localhost
        //START dev
        else if (hostName.includes("dev")) {
          if (hostName.includes("eudealernet")) {
            document.title = "Dev EU DealerNet";
          } else if (hostName.includes("apdealernet")) {
            document.title = "Dev AP DealerNet";
          } else {
            document.title = "Dev DealerNet";
          }
        }
        //END dev
        //START qa
        else if (hostName.includes("qa")) {
          if (hostName.includes("eudealernet")) {
            document.title = "QA EU DealerNet";
          } else if (hostName.includes("apdealernet")) {
            document.title = "QA AP DealerNet";
          } else {
            document.title = "QA DealerNet";
          }
        } else {
          document.title = "DealerNet";
        }
        if (!this.IsNullOrUndefined(name)) {
          document.title += " - " + name;
        }
      } catch (err) {
        console.warn("Failed Setting Development Tab Title");
      }
    },
  },
};
</script>
